import { useState } from 'react'
import { ProTable, type ProColumns } from '@ant-design/pro-components'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Checkbox } from 'antd'
import {
  reportListSchema,
  type ReportDimension,
  type ReportListQuery,
  type ReportRow
} from '@super-mega/server/contracts/analytics'
import { usePageTableNavigation } from '@/hooks/use-page-table.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getTableSortOrder } from '@/utils/list-query.ts'
import { REPORT_LABELS, REPORT_VIEWS } from './const/reports.ts'
import { reportSearchSchema, type ReportSearch } from './report-search.ts'
import { formatMoney, growth } from './utils/format.ts'
import { reportQueryOptions, reportScope } from './utils/report-query.ts'

export type ReportNavigate = (options: {
  replace?: boolean
  search: Partial<ReportSearch> | ((previous: ReportSearch) => Partial<ReportSearch>)
}) => Promise<void>

export function ReportTable({
  dimension,
  search,
  navigate
}: {
  dimension: ReportDimension
  search: ReportSearch
  navigate: ReportNavigate
}) {
  const trpc = useTRPC()
  const scope = reportScope(search, dimension)
  const view = REPORT_VIEWS[dimension]
  const query = useQuery({
    ...trpc.analytics.list.queryOptions({ ...search, ...scope, activeOnly: search.activeOnly ?? true }),
    ...reportQueryOptions,
    placeholderData: (previous, previousQuery) => {
      const input = (previousQuery?.queryKey[1] as { input?: ReportListQuery } | undefined)?.input
      // 仅同一筛选范围内翻页时保留上一页，不让其他路由或筛选的明细短暂串页。
      return input &&
        JSON.stringify(reportScope(input, dimension)) === JSON.stringify(scope) &&
        input.keyword === search.keyword &&
        input.activeOnly === (search.activeOnly ?? true)
        ? previous
        : undefined
    }
  })
  const table = usePageTableNavigation<ReportSearch>({
    search,
    navigate,
    defaultSearch: { ...search, ...reportSearchSchema.parse({}), keyword: undefined },
    getFormInitialValues: (value) => ({ keyword: value.keyword ?? '' }),
    getSearchFromForm: (value) => ({ keyword: value.keyword?.trim() || undefined }),
    showTotal: (total) => `共 ${total} 项`
  })
  const [initialValues] = useState(() => table.formInitialValues)
  const sort = (field: ReportSearch['sortField']) => getTableSortOrder(search.sortOrder, search.sortField === field)
  const columns: ProColumns<ReportRow>[] = [
    {
      title: '关键词 / ID',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { placeholder: `${REPORT_LABELS[dimension]}名称或 ID`, maxLength: 100, allowClear: true }
    },
    {
      title: 'ID',
      dataIndex: 'id',
      valueType: 'digit',
      search: false,
      width: 90,
      copyable: true,
      fieldProps: { min: 1, precision: 0 },
      render: (value, row) => (row.id === null ? '未归属' : value)
    },
    {
      title: `${REPORT_LABELS[dimension]}名称`,
      dataIndex: 'name',
      search: false,
      width: 200,
      render: (_, row) =>
        row.id === null ? (
          row.name
        ) : (
          <Button
            type="link"
            className="h-auto max-w-full p-0 text-left whitespace-normal"
            onClick={() => void navigate({ search: { ...search, view: 'charts', id: row.id!, current: 1 } })}
          >
            {row.name}
          </Button>
        )
    },
    { title: '新增订单', dataIndex: 'orders', search: false, sorter: true, sortOrder: sort('orders'), width: 110 },
    {
      title: '订单环比',
      key: 'mom',
      search: false,
      width: 160,
      render: (_, row) => growth(row.orders, row.previousOrders)
    },
    {
      title: '订单同比',
      key: 'yoy',
      search: false,
      width: 160,
      render: (_, row) => growth(row.orders, row.yearOrders)
    },
    { title: '下单客户', dataIndex: 'customers', search: false, width: 100 },
    { title: '服务对象', dataIndex: 'subjects', search: false, width: 100 },
    ...(['billed', 'received', 'refunded', 'net', 'expense', 'ledgerBalance', 'outstanding'] as const).map((key) => ({
      title: {
        billed: '新增应收 / 元',
        received: '实际收款 / 元',
        refunded: '实际退款 / 元',
        net: '净收款 / 元',
        expense: '支出金额 / 元',
        ledgerBalance: '收支结余 / 元',
        paid: '实际付款 / 元',
        paymentRefund: '付款退回 / 元',
        cashBalance: '现金收支结余 / 元',
        outstanding: '期间账单未收 / 元'
      }[key],
      dataIndex: key,
      search: false as const,
      width: 170,
      align: 'right' as const,
      sorter: [
        'billed',
        'received',
        'outstanding',
        'expense',
        'paid',
        'paymentRefund',
        'cashBalance',
        'ledgerBalance'
      ].includes(key),
      sortOrder: [
        'billed',
        'received',
        'outstanding',
        'expense',
        'paid',
        'paymentRefund',
        'cashBalance',
        'ledgerBalance'
      ].includes(key)
        ? sort(key as ReportSearch['sortField'])
        : undefined,
      renderText: (value: string) => formatMoney(value)
    })),
    {
      title: '期间在用租约',
      dataIndex: 'activeRentals',
      search: false,
      sorter: true,
      sortOrder: sort('activeRentals'),
      width: 130
    }
  ]
  return (
    <div>
      {query.isError && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          title="明细加载失败"
          description={query.error.message}
          action={<Button onClick={() => void query.refetch()}>重试</Button>}
        />
      )}
      <ProTable<ReportRow>
        columns={columns}
        rowKey={(row) => row.id ?? 'unassigned'}
        dataSource={query.isError ? [] : (query.data?.data ?? [])}
        headerTitle={view.detailTitle}
        loading={query.isFetching}
        search={{ labelWidth: 'auto' }}
        columnsState={{
          persistenceKey: `super-mega:reports:${dimension}:columns:v4`,
          persistenceType: 'localStorage',
          defaultValue: Object.fromEntries(
            ['customers', 'subjects', 'billed', 'received', 'refunded', 'net', 'outstanding', 'activeRentals'].map(
              (key) => [
                key,
                {
                  show:
                    ['received', 'refunded', 'net'].includes(key) ||
                    [...view.primary, ...view.secondary].includes(key as (typeof view.primary)[number])
                }
              ]
            )
          )
        }}
        toolBarRender={() => [
          <Checkbox
            key="active"
            checked={search.activeOnly ?? true}
            onChange={(event) => void navigate({ search: { ...search, activeOnly: event.target.checked, current: 1 } })}
          >
            仅显示有业务记录
          </Checkbox>
        ]}
        formRef={table.formRef}
        form={{ initialValues }}
        onSubmit={table.onSubmit}
        onReset={() => {
          table.formRef.current?.setFieldsValue({ keyword: '' })
          table.onReset()
        }}
        pagination={{ ...table.pagination, total: query.data?.total ?? 0 }}
        sortDirections={['descend', 'ascend', 'descend']}
        scroll={{ x: 'max-content' }}
        options={{ density: true, setting: true, reload: () => void query.refetch() }}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_, __, sorter, extra) => {
            if (extra.action !== 'sort') {
              return
            }
            const item = Array.isArray(sorter) ? sorter[0] : sorter
            const field = reportListSchema.shape.sortField.safeParse(item.field)
            void navigate({
              search: {
                ...search,
                current: 1,
                sortField: field.success && item.order ? field.data : 'orders',
                sortOrder: item.order === 'ascend' ? 'asc' : 'desc'
              }
            })
          }
          /* eslint-enable max-params */
        }
      />
    </div>
  )
}
